import { ActivityVariable } from "../types/activity";

/**
 * Per-user customization of a single activity variable.
 * Array order represents the preferred display order of variables.
 */
export interface ActivityVariablePref {
  name: string; // Variable name (matches ActivityVariable.name / ActivityVariableDefinition.name)
  unit?: string; // Preferred unit override
  hidden?: boolean; // Whether this variable column is hidden by default
}

/**
 * Map of activity preference key -> ordered list of variable preferences.
 * Stored on the user profile (UserPublicData.activityVariablePreferences).
 */
export type ActivityVariablePreferences = Record<string, ActivityVariablePref[]>;

/**
 * Compute the stable key used to store/lookup a user's variable preference for an activity.
 * Global pack activities are namespaced by their category so that different packs can't collide.
 */
export function getActivityPrefKey(params: {
  activityTypeId: string;
  globalCategoryId?: string;
}): string {
  const { activityTypeId, globalCategoryId } = params;
  return globalCategoryId
    ? `global_${globalCategoryId}_${activityTypeId}`
    : activityTypeId;
}

/**
 * Apply a saved preference to a base list of variables (derived from the activity definition).
 * - Reorders variables to match the preference order
 * - Applies unit overrides
 * - Applies hidden flags
 * Variables present on the activity but missing from the preference are appended (visible, default).
 * Preference entries referencing variables no longer on the activity are ignored.
 */
export function applyPreferenceToVariables(
  base: ActivityVariable[],
  pref?: ActivityVariablePref[],
): ActivityVariable[] {
  if (!pref || pref.length === 0) return base;

  const baseByName = new Map(base.map((v) => [v.name, v]));
  const result: ActivityVariable[] = [];
  const used = new Set<string>();

  for (const p of pref) {
    const b = baseByName.get(p.name);
    if (!b) continue;
    used.add(p.name);
    result.push({
      ...b,
      unit: p.unit ?? b.unit,
      ...(p.hidden ? { hidden: true } : {}),
    });
  }

  for (const b of base) {
    if (!used.has(b.name)) result.push(b);
  }

  return result;
}

/**
 * Build a preference array from a list of variables (the current customized order/units/hidden).
 * Used when persisting an explicit "Save" from the editing UI.
 */
export function buildPreferenceFromVariables(
  variables: ActivityVariable[],
): ActivityVariablePref[] {
  return variables.map((v) => ({
    name: v.name,
    ...(v.unit ? { unit: v.unit } : {}),
    ...(v.hidden ? { hidden: true } : {}),
  }));
}
